import React from 'react';
import { motion } from 'framer-motion';
import ServiceCard from '../components/ServiceCard';
import { useTheme } from '../context/ThemeContext';

const Services = () => {
  const { isDark } = useTheme();

  const powerPlatformServices = [
    {
      icon: '⚡',
      title: 'Power Apps Development',
      description: 'Custom low-code applications that digitize workflows and accelerate innovation with rapid time-to-value.',
      features: [
        'Canvas Apps with pixel-perfect design',
        'Model-Driven Apps on Dataverse',
        'Power Pages for external portals',
        'Mobile-first responsive apps',
        'SharePoint integration',
      ],
      badge: 'Power Platform',
      colorAccent: 'primary',
    },
    {
      icon: '🔄',
      title: 'Power Automate & RPA',
      description: 'Intelligent workflow automation that eliminates repetitive tasks and frees your team for high-value activities.',
      features: [
        'Cloud Flows for app integration',
        'Desktop Flows (RPA)',
        'Business Process Flows',
        'Multi-stage approvals',
        'Document automation',
      ],
      badge: 'Power Platform',
      colorAccent: 'primary',
    },
    {
      icon: '🤖',
      title: 'Copilot Studio & AI',
      description: 'AI-powered assistants and intelligent agents that automate conversations and enhance decision-making.',
      features: [
        'Custom Copilot development',
        'Conversational AI agents',
        'AI Builder integration',
        'Autonomous business agents',
        'Multi-channel deployment',
      ],
      badge: 'Power Platform',
      colorAccent: 'primary',
    },
  ];

  const customDevServices = [
    {
      icon: '💻',
      title: 'Web Application Development',
      description: 'Fully customized web applications built with modern technologies for complex requirements.',
      features: [
        'Full-Stack (.NET, React, Angular)',
        'Enterprise portals',
        'Progressive Web Apps (PWA)',
        'RESTful & GraphQL APIs',
        'Microservices architecture',
      ],
      badge: 'Custom Dev',
      colorAccent: 'teal',
    },
    {
      icon: '📱',
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile applications delivering optimal user experience.',
      features: [
        'Native iOS & Android',
        'Cross-platform (React Native, Flutter)',
        'Enterprise mobile solutions',
        'Offline sync capabilities',
        'MDM integration',
      ],
      badge: 'Custom Dev',
      colorAccent: 'teal',
    },
    {
      icon: '☁️',
      title: 'Azure Cloud Development',
      description: 'Cloud-native applications leveraging Azure PaaS services for scalability and resilience.',
      features: [
        'Azure Functions & Logic Apps',
        'Serverless computing',
        'Azure DevOps CI/CD',
        'Container orchestration',
        'API Management',
      ],
      badge: 'Custom Dev',
      colorAccent: 'teal',
    },
  ];

  const dataServices = [
    {
      icon: '🗄️',
      title: 'Data Warehousing & Engineering',
      description: 'Enterprise data platforms that consolidate, transform, and organize your data for analytics.',
      features: [
        'Azure Synapse Analytics',
        'Azure Databricks',
        'Data Lake implementation',
        'ETL/ELT pipelines (Data Factory)',
        'Dimensional modeling',
      ],
      badge: 'Data Services',
      colorAccent: 'sky',
    },
    {
      icon: '📊',
      title: 'Business Intelligence',
      description: 'Transform raw data into actionable insights with stunning visualizations and interactive dashboards.',
      features: [
        'Power BI dashboards & reports',
        'Embedded analytics',
        'Self-service BI training',
        'KPI frameworks',
        'Real-time data visualization',
      ],
      badge: 'Data Services',
      colorAccent: 'sky',
    },
    {
      icon: '🧠',
      title: 'Advanced Analytics & ML',
      description: 'Predictive analytics and machine learning solutions that unlock deeper insights from your data.',
      features: [
        'Azure Machine Learning',
        'Predictive models',
        'Real-time stream analytics',
        'Azure Cognitive Services',
        'Data science consulting',
      ],
      badge: 'Data Services',
      colorAccent: 'sky',
    },
  ];

  const xeroService = [
    {
      icon: '💰',
      title: 'Xero Implementation & Integration',
      description: 'End-to-end Xero implementation and backend integration services to streamline your accounting processes.',
      features: [
        'Complete Xero setup & configuration',
        'Backend system integration',
        'Automated financial workflows',
        'Bank feeds & reconciliation',
        'Custom reporting & dashboards',
      ],
      badge: 'Accounting',
      colorAccent: 'emerald',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      <section className="py-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold font-manrope mb-6">
              Our <span className="text-primary">Services</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-3xl mx-auto leading-relaxed">
              Comprehensive technology services spanning low-code platforms, custom development, and enterprise data solutions — tailored to your unique business challenges.
            </p>
          </motion.div>

          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-3xl font-bold font-manrope mb-8 text-center md:text-left">
              <span className="text-primary">Microsoft Power Platform</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {powerPlatformServices.map((service, index) => (
                <ServiceCard key={index} {...service} />
              ))}
            </div>
          </motion.div>

          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="text-3xl font-bold font-manrope mb-8 text-center md:text-left">
              <span className="text-accent-orange">Custom Development</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {customDevServices.map((service, index) => (
                <ServiceCard key={index} {...service} />
              ))}
            </div>
          </motion.div>

          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="text-3xl font-bold font-manrope mb-8 text-center md:text-left">
              <span className="text-accent-purple">Data Services</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {dataServices.map((service, index) => (
                <ServiceCard key={index} {...service} />
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <h2 className="text-3xl font-bold font-manrope mb-8 text-center md:text-left">
              <span className="text-accent-xero">Accounting Integration</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {xeroService.map((service, index) => (
                <ServiceCard key={index} {...service} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;